import { useForm } from 'react-hook-form';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertService } from '../../../../services/alert.service';
import { ChannelsService } from '../../../../services/channels.service';
import useInputValidation from '../../../../hooks/use-input-validation.hook';
import Input from '../../../../components/forms/input.component';
import Button from '../../../../components/forms/button.component';
import useModal from '../../../../hooks/use-close-modal.hook';
import LogData from '../../../../components/logs/log-data.component';
import Icon from '../../../../components/forms/icon.component';
import ReCAPTCHA from 'react-google-recaptcha';
import { useDarkMode } from '../../../../hooks/use-dark-mode.hook';

interface SendMessageProps {
  show: boolean;
  onRequestClose: (refresh: boolean) => void;
  payload: any;
}

function SendMessage({ show, onRequestClose, payload }: SendMessageProps) {
  const { t } = useTranslation();
  const modalRef = useRef<HTMLDivElement>(null);
  const { tenDigitsRegex, handleOnlyNumbers } = useInputValidation();
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const isDarkTheme = useDarkMode();

  useModal(modalRef, show);
  const defaultValues = {
    phone_number: '',
    waba_account: ''
  };

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isValid, isSubmitting }
  } = useForm({
    mode: 'onChange',
    defaultValues
  });

  const handleCaptchaChange = (token: string | null) => {
    setRecaptchaToken(token);
  };
  const sendMessage = async (data: any, buttonId: string) => {
    if (!recaptchaToken) {
      AlertService.toastError(t('general.recaptcha-required'));
      return;
    }
    try {
      const confirmed = await AlertService.question(
        t('general.atention'),
        `${t('channels.wa.q5')}, ${data.phone_number}`,
        null as any
      );
      if (!confirmed) {
        return;
      }
      const submitData = {
        ...data,
        recaptcha: recaptchaToken
      };
      await ChannelsService.sendMessageTestWA(submitData);
      await onRequestClose(true);
      AlertService.toastSuccess(t('channels.wa.success3'));
      reset(defaultValues);
      recaptchaRef.current?.reset();
      setRecaptchaToken(null);
      LogData(data, buttonId, window.location.href);
    } catch (errorCode: any) {
      console.log('Error:', errorCode);
      AlertService.toastError(errorCode.error || errorCode);
      recaptchaRef.current?.reset();
      setRecaptchaToken(null);
    }
  };

  const resetForm = () => {
    onRequestClose(false);
    reset(defaultValues);
    recaptchaRef.current?.reset();
    setRecaptchaToken(null);
  };

  useEffect(() => {
    if (payload?._id) {
      reset({
        phone_number: '',
        waba_account: payload._id
      });
      recaptchaRef.current?.reset();
      setRecaptchaToken(null);
    }
  }, [payload, reset]);

  return (
    <div className="modal fade" ref={modalRef} data-bs-backdrop="static">
      <div className="modal-dialog">
        <div className="modal-content">
          <div className="modal-header justify-content-end border-0 pb-0">
            <div
              className="btn btn-sm btn-icon btn-active-light-primary"
              onClick={() => resetForm()}
            >
              <Icon icon="ki-cross" size="fs-2x" />
            </div>
          </div>
          <form onSubmit={handleSubmit((data) => sendMessage(data, 'sendMessageTest-34463874687'))}>
            <div className="modal-body pt-0 pb-15 px-5 px-xl-20">
              <div className="mt-1 text-center">
                <h1>{t('channels.title-send')}</h1>
                <h5 className="text-primary fw-bolder">{payload?.name}</h5>
                <div className="text-muted fw-semibold fs-5">
                  <span
                    dangerouslySetInnerHTML={{ __html: t('channels.rcs.modal-C-Subtitle') }}
                  ></span>
                </div>
                <div className="separator border-2 my-5"></div>
              </div>
              <div className="row my-10 px-10">
                <div className="col-lg-12 mb-10">
                  <div className="fv-row mb-0">
                    <Input
                      label={t('channels.wa.number')}
                      requiredIndicator="required"
                      placeholder={t('channels.wa.placecholder-number')}
                      errors={errors.phone_number}
                      onKeyDown={handleOnlyNumbers}
                      {...register('phone_number', {
                        required: true,
                        setValueAs: (value) => value.trim(),
                        pattern: {
                          value: tenDigitsRegex,
                          message: t('channels.wa.validate-1')
                        }
                      })}
                    />
                  </div>
                </div>
                <div className="d-flex justify-content-center mb-5">
                  <ReCAPTCHA
                    sitekey="6Lfec20rAAAAANyGkUlot_jUEWAbexPdSk8Nw_lu"
                    onChange={handleCaptchaChange}
                    theme={isDarkTheme ? 'dark' : 'light'}
                    ref={recaptchaRef}
                  />
                </div>
              </div>
              <div className="d-flex flex-center flex-row-fluid pt-0">
                <button
                  type="button"
                  className="btn btn-light hover-scale"
                  onClick={() => resetForm()}
                >
                  {t('general.close')}
                </button>
                <Button
                  type="submit"
                  className="btn btn-primary ms-3 hover-scale"
                  disabled={!isValid || !recaptchaToken}
                  isLoading={isSubmitting}
                >
                  {t('general.send')}
                  <Icon icon="ki-send" size="fs-1" className="ms-2" />
                </Button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default SendMessage;
